import React, { useCallback, useMemo } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { ScreenHeader as GarnishScreenHeader } from '@sg/garnish';
import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import { useTelemetry } from '@order/Telemetry';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useLastInteractedStore } from '../../context/LastInteractedStore';
import { useLoyaltyAvailablePoints } from '../../hooks/useLoyaltyAvailablePoints';
import {
  useScreenHeaderAddressQuery,
  useScreenHeaderRestaurantQuery,
} from './graphql/ScreenHeader.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const ScreenHeader = (props: Props) => {
  const { pendingOrder, rounded } = props;

  const router = useRouter();
  const { track } = useTelemetry();
  const { formatMessage, formatNumber } = useIntl();

  const isLoggedIn = useIsLoggedIn();
  const lastInteractedStore = useLastInteractedStore();
  const points = useLoyaltyAvailablePoints();

  const addressId = lastInteractedStore?.addressId ?? '';
  const restaurantId = lastInteractedStore?.restaurantId ?? '';

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [{ data: addressData, fetching: isFetchingAddress }] = useScreenHeaderAddressQuery({
    variables: { addressId },
    pause: !addressId,
    requestPolicy: 'cache-first',
  });

  const [{ data: restaurantData, fetching: isFetchingRestaurant }] = useScreenHeaderRestaurantQuery(
    {
      variables: { id: restaurantId },
      pause: !restaurantId,
      requestPolicy: 'cache-first',
    },
  );

  // ─── Derived ───────────────────────────────────────────────────────────────

  const addressName = addressData?.address?.name;
  const restaurant = restaurantData?.restaurant;

  const orderChannel = useMemo(() => {
    if (addressId) return 'delivery';
    if (restaurant?.isOutpost) return 'outpost';

    return 'pickup';
  }, [addressId, restaurant?.isOutpost]);

  const locationName = addressId ? addressName : restaurant?.name;
  const locationTitle = locationName ?? formatMessage(messages.findLocationNearYou);
  const headerEyebrow = formatMessage(messages.headerEyebrow, { orderChannel });
  const pendingOrderStatus = pendingOrder
    ? formatMessage(messages.pendingOrderStatus, { orderStatus: pendingOrder.orderStatus })
    : undefined;
  const pendingOrderEta = pendingOrder
    ? formatMessage(messages.pendingOrderEta, { eta: pendingOrder.eta })
    : undefined;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleLocationPress = useCallback(() => {
    router.navigate('/locations');
  }, [router]);

  const handleRewardsPress = useCallback(() => {
    if (isLoggedIn) {
      track('home.loyalty.cta_tapped');
      router.navigate('/rewards');

      return;
    }

    track('home.banner.cta_tapped', { destination: 'SignIn' });
    router.navigate({
      pathname: '/(auth)/join-or-sign-in',
      params: { redirect: '/' },
    });
  }, [isLoggedIn, router, track]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <GarnishScreenHeader.Container palette={pendingOrder ? 'spinach' : 'brown'} rounded={rounded}>
      <GarnishScreenHeader.Content>
        {pendingOrder && pendingOrderStatus && pendingOrderEta ? (
          <TouchableOpacity
            accessibilityLabel={`${pendingOrderStatus}, ${pendingOrderEta}`}
            accessibilityRole="button"
            activeOpacity={0.8}
            onPress={pendingOrder.onPress}
            style={styles.pendingOrderButton}
          >
            <GarnishScreenHeader.Eyebrow>{pendingOrderStatus}</GarnishScreenHeader.Eyebrow>

            <GarnishScreenHeader.Title>{pendingOrderEta}</GarnishScreenHeader.Title>
          </TouchableOpacity>
        ) : (
          <GarnishScreenHeader.TitleContainer>
            {lastInteractedStore ? (
              <GarnishScreenHeader.Eyebrow>{headerEyebrow}</GarnishScreenHeader.Eyebrow>
            ) : null}

            {isFetchingAddress || isFetchingRestaurant ? null : (
              <GarnishScreenHeader.LocationButton onPress={handleLocationPress}>
                {locationTitle}
              </GarnishScreenHeader.LocationButton>
            )}
          </GarnishScreenHeader.TitleContainer>
        )}

        {isLoggedIn ? (
          <GarnishScreenHeader.PointsButton
            points={formatNumber(points)}
            text={formatMessage(messages.points)}
            onPress={handleRewardsPress}
          />
        ) : (
          <GarnishScreenHeader.PointsButton
            text={formatMessage(messages.signIn)}
            onPress={handleRewardsPress}
          />
        )}
      </GarnishScreenHeader.Content>
    </GarnishScreenHeader.Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pendingOrderButton: {
    flexShrink: 1,
    alignItems: 'flex-start',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  headerEyebrow: {
    defaultMessage:
      '{orderChannel, select, delivery {Deliver to} pickup {Pickup from} outpost {Outpost at} other {Deliver to}}',
    description: 'Screen header | Location eyebrow',
  },
  findLocationNearYou: {
    defaultMessage: 'Find location near you',
    description: 'Screen header | Find location near you button',
  },
  points: {
    defaultMessage: 'pts',
    description: 'Screen header | Points button',
  },
  signIn: {
    defaultMessage: 'Sign in',
    description: 'Screen header | Sign in button',
  },
  pendingOrderStatus: {
    defaultMessage:
      '{orderStatus, select, received {Order received} preparing {Preparing order} readyForCourier {Awaiting courier} delivering {Delivering order} other {Order in progress}}',
    description: 'Screen header | Pending order status',
  },
  pendingOrderEta: {
    defaultMessage: 'ETA {eta}',
    description: 'Screen header | Pending order ETA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  pendingOrder?: PendingOrder;
  rounded?: boolean;
};

type PendingOrder = {
  eta: string;
  orderStatus: FlattenedOrderStatuses;
  onPress: () => void;
};
